
import { supabase } from '../client';

// Cache pour l'ID d'organisation
let organizationIdCache: string | null = null;

/**
 * Obtenir l'ID de l'organisation de l'utilisateur actuel
 * Utilise un cache pour éviter de multiples appels API pour la même information
 */
export const getCurrentUserOrganizationId = async (): Promise<string | null> => {
  // Vérifier d'abord dans localStorage
  const storedOrgId = localStorage.getItem('currentOrganizationId');
  if (storedOrgId) {
    console.log("Using organization ID from localStorage:", storedOrgId);
    // Mettre à jour le cache aussi
    organizationIdCache = storedOrgId;
    return storedOrgId;
  }
  
  // Retourner la valeur mise en cache si disponible
  if (organizationIdCache) {
    console.log("Using cached organization ID:", organizationIdCache);
    // Stocker dans localStorage pour les accès futurs
    localStorage.setItem('currentOrganizationId', organizationIdCache);
    return organizationIdCache;
  }
  
  try {
    // Obtenir les données de l'utilisateur
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.error("Error getting user:", userError);
      return null;
    }
    
    // Requête pour l'organisation où l'utilisateur est membre
    const { data: memberData, error: memberError } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userData.user.id)
      .single();
    
    if (memberError || !memberData) {
      console.error("Error getting organization membership:", memberError);
      
      // Essayer de récupérer depuis la table organizations directement (pour les propriétaires)
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', userData.user.id)
        .single();
      
      if (orgError || !orgData) {
        console.error("Error getting organization:", orgError);
        return null;
      }
      
      // Mettre en cache et stocker l'ID de l'organisation
      organizationIdCache = orgData.id;
      localStorage.setItem('currentOrganizationId', orgData.id);
      console.log("Found organization ID through owner lookup:", organizationIdCache);
      return organizationIdCache;
    }
    
    // Mettre en cache et stocker l'ID de l'organisation de la table des membres
    organizationIdCache = memberData.organization_id;
    localStorage.setItem('currentOrganizationId', memberData.organization_id);
    console.log("Found organization ID through member lookup:", organizationIdCache);
    return organizationIdCache;
  } catch (error) {
    console.error("Exception getting organization ID:", error);
    return null;
  }
};

/**
 * Définir le cache d'ID d'organisation
 */
export const setOrganizationIdCache = (id: string | null): void => {
  organizationIdCache = id;
  if (id) {
    localStorage.setItem('currentOrganizationId', id);
  } else {
    localStorage.removeItem('currentOrganizationId');
  }
};

/**
 * Effacer le cache d'organisation
 */
export const clearOrganizationCache = (): void => {
  organizationIdCache = null;
  localStorage.removeItem('currentOrganizationId');
};
